export const PADDING = 32; // Matches the spread of the box-shadow on the appContainer
export const LAUNCHER_BUTTON_NO_PADDING = 56;
const APP_HEIGHT_NO_PADDING = 560;
const APP_WIDTH_NO_PADDING = 400;

const LAUNCHER_BUTTON = PADDING * 2 + LAUNCHER_BUTTON_NO_PADDING;
const APP_WIDTH = PADDING * 2 + APP_WIDTH_NO_PADDING;
const APP_HEIGHT_WITHOUT_LAUNCHER_BUTTON = PADDING * 2 + APP_HEIGHT_NO_PADDING;
const APP_HEIGHT_WITH_LAUNCHER_BUTTON =
  APP_HEIGHT_WITHOUT_LAUNCHER_BUTTON + LAUNCHER_BUTTON_NO_PADDING;

const PROACTIVE_CHAT_MARGIN = 8;

const EXPANDED_MAX_WIDTH_PX = 1000;
const EXPANDED_MAX_HEIGHT = 2000;
const EXPANDED_MAX_WIDTH_PCT = 90;
const EXPANDED_MAX_HEIGHT_PCT = 90;

interface BaseReturn {
  width: string;
  height: string;
}
interface FullscreenReturn extends BaseReturn {
  type: "fullscreen";
}
interface NormalReturn extends BaseReturn {
  type: "normal";
  widthPx: number;
  heightPx: number;
}

export function getIframeDimensions({
  isOpen,
  showLauncherButton,
  offsetBottom,
  offsetSide,
  parentViewDimensions,
  proactiveChatDimensions = { width: 0, height: 0 },
}: {
  isOpen: boolean;
  showLauncherButton?: boolean;
  offsetBottom: number;
  offsetSide: number;
  parentViewDimensions: { height: number; width: number } | "fullscreen";
  proactiveChatDimensions?: { width: number; height: number };
}): FullscreenReturn | NormalReturn {
  if (isOpen) {
    if (isFullscreenMode({ parentViewDimensions, showLauncherButton, offsetSide })) {
      return { width: "100%", height: "100%", type: "fullscreen" };
    }
    const width = APP_WIDTH;
    const height = showLauncherButton
      ? APP_HEIGHT_WITH_LAUNCHER_BUTTON
      : APP_HEIGHT_WITHOUT_LAUNCHER_BUTTON;
    return {
      width: `${width}px`,
      height: cssHeightCalc(height, offsetBottom),
      type: "normal",
      widthPx: width,
      heightPx: height,
    };
  }

  const closedWidth = LAUNCHER_BUTTON + proactiveChatDimensions.width + PROACTIVE_CHAT_MARGIN;
  const closedHeight = Math.max(LAUNCHER_BUTTON, proactiveChatDimensions.height + PADDING);

  return showLauncherButton
    ? {
        width: `${closedWidth}px`,
        height: `${closedHeight}px`,
        type: parentViewDimensions === "fullscreen" ? "fullscreen" : "normal",
        widthPx: closedWidth,
        heightPx: closedHeight,
      }
    : {
        width: "0",
        height: "0",
        type: parentViewDimensions === "fullscreen" ? "fullscreen" : "normal",
        widthPx: 0,
        heightPx: 0,
      };
}

export function getExpandedIframeDimensions({
  isOpen,
  isExpanded,
  showLauncherButton,
  offsetBottom,
  offsetSide,
  parentViewDimensions,
  proactiveChatDimensions,
}: {
  isOpen: boolean;
  isExpanded: boolean;
  showLauncherButton?: boolean;
  offsetBottom: number;
  offsetSide: number;
  parentViewDimensions: { height: number; width: number } | "fullscreen";
  proactiveChatDimensions?: { width: number; height: number };
}): BaseReturn & { type: "fullscreen" | "normal" } {
  const iframeDimensions = getIframeDimensions({
    isOpen,
    showLauncherButton,
    offsetBottom,
    offsetSide,
    parentViewDimensions,
    proactiveChatDimensions,
  });
  if (!isOpen || iframeDimensions.type === "fullscreen") {
    return iframeDimensions;
  }

  if (parentViewDimensions === "fullscreen") {
    return { width: "100%", height: "100%", type: "fullscreen" };
  }

  if (isExpanded) {
    const expandedIframeDimensions = {
      width: Math.min(
        EXPANDED_MAX_WIDTH_PX,
        (EXPANDED_MAX_WIDTH_PCT / 100) * parentViewDimensions.width,
      ),
      height: Math.min(
        EXPANDED_MAX_HEIGHT,
        (EXPANDED_MAX_HEIGHT_PCT / 100) * parentViewDimensions.height,
      ),
    };
    return {
      width: Math.max(iframeDimensions.widthPx, expandedIframeDimensions.width) + "px",
      height: cssHeightCalc(
        Math.max(iframeDimensions.heightPx, expandedIframeDimensions.height),
        offsetBottom,
      ),
      type: "normal",
    };
  } else {
    return iframeDimensions;
  }
}

export function isFullscreenMode({
  parentViewDimensions,
  showLauncherButton,
  offsetSide,
}: {
  parentViewDimensions: { height: number; width: number } | "fullscreen";
  showLauncherButton?: boolean;
  offsetSide: number;
}) {
  switch (parentViewDimensions) {
    case "fullscreen":
      return true;
    default:
      return showLauncherButton
        ? parentViewDimensions.width < APP_WIDTH + offsetSide
        : parentViewDimensions.width < APP_WIDTH + offsetSide;
  }
}

/**
 * Ensure the iframe collpases in height for smaller screens
 */
const cssHeightCalc = (height: number, offsetBottom: number) =>
  `min(${height}px, 100% - ${offsetBottom}px)`;
